import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase F-finale-7 Slice 7b.1 — single source of truth for the IA-side
 * seat-assignment-config write-gate.
 *
 * Mirrors `useCanEditAdmissions` (Slice 6b) but is RBAC-only: unlike
 * admissions/forms there is no `seating_editable_by_tenant` tenant pause
 * flag on the BE today, so the only signal is whether the principal holds
 * `manage_zones` (base role catalog or designation override). `admin`
 * carries it by default; staff roles without an override do not.
 *
 * Returns:
 *   - `canEditSeating` — overall write-eligibility. The editor's write
 *     controls (mode picker, switches, SaveBar, Reset) render only when
 *     `true`; otherwise the read-only `PermissionNotice` shows.
 *   - `missingRbac` — logged in but lacks `manage_zones`.
 *
 * Vertical gating (study-centre only) is applied at the nav-construction
 * site (`applySeatConfigNavEntry` is fed `canEditSeating && vertical ===
 * "study_centre"`), not here, so this hook stays pure + `useAuth`-only and
 * trivially unit-testable — matching `useCanEditAdmissions`.
 */
export interface SeatingAuthoringGate {
  canEditSeating: boolean;
  missingRbac: boolean;
}

export function useCanEditSeating(): SeatingAuthoringGate {
  const { user } = useAuth();
  return React.useMemo<SeatingAuthoringGate>(() => {
    if (!user) {
      return { canEditSeating: false, missingRbac: false };
    }
    const hasRbac = userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_zones",
      user.permission_mode ?? "additive",
    );
    return {
      canEditSeating: hasRbac,
      missingRbac: !hasRbac,
    };
  }, [user]);
}

/**
 * Phase F-finale-7 Slice 7b.4d — the IA-side booking admin write-gate.
 *
 * Distinct from `useCanEditSeating` (`manage_zones`): the dynamic-mode
 * booking admin surface (list + check-in + cancel) is guarded BE-side by
 * `_BOOKING_WRITE_GUARD` = `manage_bookings` OR `book_seat`. `book_seat` is
 * the student-facing booking-creation permission, so the *admin* surface
 * keys off `manage_bookings` — the read guard would 403 a principal who
 * holds neither, which is why the Bookings nav entry + list query gate on
 * this hook rather than on `manage_zones`.
 *
 * `admin` carries `manage_bookings` by default (shared role catalog), so it
 * lands alongside the rest of the seat-management cluster; staff without an
 * override do not. Vertical gating (study-centre only) is applied at the
 * nav-construction site, matching `useCanEditSeating`.
 *
 * Returns:
 *   - `canManageBookings` — overall write-eligibility (check-in / cancel).
 *   - `missingRbac` — logged in but lacks `manage_bookings`.
 */
export interface BookingAuthoringGate {
  canManageBookings: boolean;
  missingRbac: boolean;
}

export function useCanManageBookings(): BookingAuthoringGate {
  const { user } = useAuth();
  return React.useMemo<BookingAuthoringGate>(() => {
    if (!user) {
      return { canManageBookings: false, missingRbac: false };
    }
    const hasRbac = userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_bookings",
      user.permission_mode ?? "additive",
    );
    return {
      canManageBookings: hasRbac,
      missingRbac: !hasRbac,
    };
  }, [user]);
}
